import { IkeToken, type OrgSearchResults } from '@ikegps/core-api';
import { observable, makeObservable } from 'mobx';
import { Icon } from '../components/icon';
import { MaterialIcon } from '../components/icon/icons';
import { Table } from '../components/table/table';
import { addStyles } from '../components/theme';
import { T, Translate } from '../i18n';
import { m, type MithrilVNode } from '../mithril';
import { Services } from '../services';
import { CSS, Style } from './org.search.style';
import { IkePage, type IkePageConfig, IkePageName } from './page';

addStyles(Style);

export const OrgSearchConfig: IkePageConfig = {
	type: 'internal',
	name: IkePageName.orgSearch,
	path: '/org/search',
	auth: {
		required: true,
		tokens: [IkeToken.ORG_SEARCH_PAGE],
	},
	nav: {
		name: T.ORG_SEARCH,
		icon: MaterialIcon.ORG_SEARCH,
	},
};

export class OrgSearchPage extends IkePage {
	private rows: MithrilVNode[][] = [];
	private timer: ReturnType<typeof setTimeout> | null = null;

	@observable private searchResponse: OrgSearchResults[] = null;
	@observable private searchQuery: string = null;

	constructor() {
		super(OrgSearchConfig);
		makeObservable(this);
	}

	private viewPole(primary: string, secondary: string, department: string, index: number): MithrilVNode[] {
		return [
			m('span', { className: CSS.tableCell }, String(index)),
			m('span', { className: CSS.tableCell }, `${primary == null ? Translate(T.EMPTY) : primary}`),
			m('span', { className: CSS.tableCell }, `${secondary == null ? Translate(T.EMPTY) : secondary}`),
			m('span', { className: CSS.tableCell }, `${department == null ? Translate(T.EMPTY) : department}`),
		];
	}

	private viewPoles(): MithrilVNode {
		const headers = ['#', 'Primary', 'Secondary', 'Department'];

		while (this.rows.length > 0) {
			this.rows.pop();
		}

		const results = this.searchResponse;

		if (results != null && this.searchQuery != null) {
			let count = 0;
			results.forEach((el) => {
				count++;
				this.rows.push(this.viewPole(el.primary, el.secondary, el.departmentName, count));
			});
		}

		if (this.rows.length > 0) {
			//results
			return Table.view(Translate(T.SEARCH_RES), headers, this.rows);
		} else if (this.searchQuery != null) {
			//query but no results
			return Table.view(Translate(T.TAG_FILTER__SEARCH_EMPTY), [], this.rows);
		} else {
			return Table.view(Translate(T.INPUT_REQUIRED), [], this.rows);
		}
	}

	private async sendQ(str: string | null) {
		if (str === '' || str == null) {
			this.searchResponse = null;
			this.searchQuery = null;
		} else {
			this.searchQuery = str;
			this.searchResponse = await Services.SearchOrg.sendQuery(this.searchQuery);
		}
		return true;
	}

	private onChange(e: Event) {
		clearTimeout(this.timer);

		this.timer = setTimeout(
			(val) => {
				// eslint-disable-next-line @typescript-eslint/no-floating-promises
				this.sendQ(val);
			},
			300,
			(e.target as HTMLInputElement).value
		);
	}

	get nodes(): MithrilVNode {
		if (!Services.User.hasUser) {
			// eslint-disable-next-line @typescript-eslint/no-floating-promises
			Services.User.logout();
			return null;
		}

		if (!Services.Token.has(IkeToken.ORG_SEARCH_PAGE) && !Services.Token.has(IkeToken.FEATURE_GROUP_IKE_ANALYZE)) {
			const lockedOut = m('div', { className: 'container' }, [
				m('span', { className: 'title' }, 'No Organization Search Feature Token'),
			]);

			return lockedOut;
		}

		const icon = Icon.view(MaterialIcon.ORG_SEARCH, { className: CSS.box.icon });

		let children = [
			m('header', { className: CSS.search.container }, [
				m('div', { className: CSS.search.row }, [
					m('div', { className: CSS.search.section }, [m('span', { className: 'title' }, Translate(T.ORG_SEARCH))]),
					m('div', { className: CSS.box.container }, [
						icon,
						m('input', {
							className: CSS.box.input,
							type: 'text',
							placeholder: 'search by secondary or primary field',
							oninput: (e) => this.onChange(e),
						}),
					]),
					m('div', { className: `${CSS.search.section} ${CSS.search.sectionRight}` }),
				]),
			]),
			m('div', { className: 'results' }, [this.viewPoles()]),
		];
		const nodes = m('div', { className: 'container' }, children);

		return nodes;
	}
}
